/**
 * Pure layout math for "gameless" mode: fitting `count` square tiles into a
 * container as large as possible, choosing whichever column count yields the
 * biggest square tile for the container's actual width/height.
 */

export interface SquareGridLayout {
  columns: number
  rows: number
  /** Side length of each square tile, in pixels. */
  tileSize: number
}

const EMPTY_LAYOUT: SquareGridLayout = { columns: 0, rows: 0, tileSize: 0 }

/**
 * Computes the column count and tile size that maximizes square tile size
 * for `count` tiles inside a `containerWidth` x `containerHeight` box.
 *
 * Tries every possible column count from 1 to `count` and keeps whichever
 * one lets tiles be the largest, respecting `gap` between tiles. The chosen
 * shape (columns/rows) is based on the uncapped best fit; `maxTileSize` only
 * clamps the final tile size, so a big screen with few mates doesn't blow
 * tiles up arbitrarily large.
 */
export function computeSquareGridLayout(
  count: number,
  containerWidth: number,
  containerHeight: number,
  gap: number = 16,
  maxTileSize: number = Infinity
): SquareGridLayout {
  if (count <= 0 || containerWidth <= 0 || containerHeight <= 0) {
    return EMPTY_LAYOUT
  }

  let best: SquareGridLayout = EMPTY_LAYOUT

  for (let columns = 1; columns <= count; columns++) {
    const rows = Math.ceil(count / columns)
    const tileWidth = (containerWidth - gap * (columns - 1)) / columns
    const tileHeight = (containerHeight - gap * (rows - 1)) / rows
    const tileSize = Math.min(tileWidth, tileHeight)

    if (tileSize > best.tileSize) {
      best = { columns, rows, tileSize }
    }
  }

  return { ...best, tileSize: Math.max(0, Math.floor(Math.min(best.tileSize, maxTileSize))) }
}
